<template>
	<div>
		<div class="subtitle" v-if="heading">{{ heading }}</div>

		<div class="box">
			<table class="table is-fullwidth is-hidden-mobile">
				<thead>
					<tr>
						<th></th>
						<th class="has-text-right">#</th>
						<th>Team</th>
						<th class="has-text-centered">Record</th>
						<th class="has-text-centered" v-if="has_divisions">Division</th>
						<th class="has-text-centered">Streak</th>
						<th title="Points For" class="has-text-right">
							<span class="title-text">PF</span>
						</th>
						<th title="Points Against" class="has-text-right">
							<span class="title-text">PA</span>
						</th>
						<th class="has-text-right">Power</th>
						<th title="Lineup Accuracy" class="has-text-right">
							<span class="title-text">Acc.</span>
						</th>
						<th title="Points Left On Bench" class="has-text-right">
							<span class="title-text">PB</span>
						</th>
						<th class="has-text-right">Luck</th>
						<th title="Strength of Schedule" class="has-text-right">
							<span class="title-text">SOS</span>
						</th>
						<template v-if="has_all_records">
							<th title="All-Play Record" class="has-text-centered">
								<span class="title-text">APR</span>
							</th>
							<th title="All-Play Standing" class="has-text-centered">
								<span class="title-text">APS</span>
							</th>
						</template>
						<template v-if="has_median_records">
							<th title="Median-Play Record" class="has-text-centered">
								<span class="title-text">MPR</span>
							</th>
							<th title="Median-Play Standing" class="has-text-centered">
								<span class="title-text">MPS</span>
							</th>
						</template>
					</tr>
				</thead>
				<tbody>
					<tr v-for="team in teams">
						<td class="has-text-right">
							<font-awesome-icon
								v-if="team.standing_change"
								:icon="['far', standing_change_to_icon(team.standing_change)]"
								:class="team.standing_change > 0 ? 'has-text-success' : 'has-text-danger'"
							/>
						</td>
						<td class="has-text-right">{{ team.standing_rank }}</td>
						<td><TeamLink :teamKey="team.key" :teamName="team.name" /></td>
						<td class="has-text-centered">{{ team.record }}</td>
						<td class="has-text-centered" v-if="has_divisions">{{ team.division_record }}</td>
						<td class="has-text-centered">{{ team.streak }}</td>
						<td class="has-text-right">{{ team.points_for }}</td>
						<td class="has-text-right">{{ team.points_against }}</td>
						<td class="has-text-right">{{ team.power_rating }}</td>
						<td class="has-text-right">{{ team.lineup_accuracy }}</td>
						<td class="has-text-right">{{ team.points_left_on_bench }}</td>
						<td class="has-text-right">{{ team.luck }}</td>
						<td class="has-text-right">{{ team.strength_of_schedule }}</td>
						<template v-if="has_all_records">
							<td class="has-text-centered">{{ team.all_record }}</td>
							<td class="has-text-centered">{{ team.all_standing_rank }}</td>
						</template>
						<template v-if="has_median_records">
							<td class="has-text-centered">{{ team.median_record }}</td>
							<td class="has-text-centered">{{ team.median_standing_rank }}</td>
						</template>
					</tr>
				</tbody>
			</table>

			<table v-if="!is_mobile_shown_more" class="table is-fullwidth is-hidden-tablet">
				<thead>
					<tr>
						<th class="has-text-right">#</th>
						<th>Team</th>
						<th class="has-text-centered">Record</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="team in teams">
						<td class="has-text-right">{{ team.standing_rank }}</td>
						<td><TeamLink :teamKey="team.key" :teamName="team.name" /></td>
						<td class="has-text-centered">{{ team.record }}</td>
					</tr>
				</tbody>
			</table>

			<div v-else class="table-scroll-container">
				<div class="table-scroll">
					<table class="table is-fullwidth is-hidden-tablet">
						<thead>
							<tr>
								<th class="has-text-right">#</th>
								<th class="has-text-centered">Record</th>
								<th class="has-text-centered" v-if="has_divisions">Division</th>
								<th class="has-text-centered">Streak</th>
								<th title="Points For" class="has-text-right">
									<span class="title-text">PF</span>
								</th>
								<th title="Points Against" class="has-text-right">
									<span class="title-text">PA</span>
								</th>
								<th class="has-text-right">Power</th>
								<th title="Lineup Accuracy" class="has-text-right">
									<span class="title-text">Acc.</span>
								</th>
								<th title="Points Left On Bench" class="has-text-right">
									<span class="title-text">PB</span>
								</th>
								<th class="has-text-right">Luck</th>
								<th title="Strength of Schedule" class="has-text-right">
									<span class="title-text">SOS</span>
								</th>
								<template v-if="has_all_records">
									<th title="All-Play Record" class="has-text-centered">
										<span class="title-text">APR</span>
									</th>
									<th title="All-Play Standing" class="has-text-centered">
										<span class="title-text">APS</span>
									</th>
								</template>
								<template v-if="has_median_records">
									<th title="Median-Play Record" class="has-text-centered">
										<span class="title-text">MPR</span>
									</th>
									<th title="Median-Play Standing" class="has-text-centered">
										<span class="title-text">MPS</span>
									</th>
								</template>
							</tr>
						</thead>
						<tbody>
							<template v-for="team in teams">
								<tr class="has-next-row">
									<td class="has-text-right">
										<span class="standing-rank">
											<font-awesome-icon
												v-if="team.standing_change"
												:icon="['far', standing_change_to_icon(team.standing_change)]"
												:class="team.standing_change > 0 ? 'has-text-success' : 'has-text-danger'"
											/>
											{{ team.standing_rank }}
										</span>
									</td>
									<td :colspan="9 + has_divisions + (has_all_records ? 2 : 0) + (has_median_records ? 2 : 0)">
										<TeamLink :teamKey="team.key" :teamName="team.name" />
									</td>
								</tr>
								<tr>
									<td></td>
									<td class="has-text-centered">{{ team.record }}</td>
									<td class="has-text-centered" v-if="has_divisions">{{ team.division_record }}</td>
									<td class="has-text-centered">{{ team.streak }}</td>
									<td class="has-text-right">{{ team.points_for }}</td>
									<td class="has-text-right">{{ team.points_against }}</td>
									<td class="has-text-right">{{ team.power_rating }}</td>
									<td class="has-text-right">{{ team.lineup_accuracy }}</td>
									<td class="has-text-right">{{ team.points_left_on_bench }}</td>
									<td class="has-text-right">{{ team.luck }}</td>
									<td class="has-text-right">{{ team.strength_of_schedule }}</td>
									<template v-if="has_all_records">
										<td class="has-text-centered">{{ team.all_record }}</td>
										<td class="has-text-centered">{{ team.all_standing_rank }}</td>
									</template>
									<template v-if="has_median_records">
										<td class="has-text-centered">{{ team.median_record }}</td>
										<td class="has-text-centered">{{ team.median_standing_rank }}</td>
									</template>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<div class="cta-box has-text-centered is-hidden-tablet" v-if="!is_mobile_shown_more">
			<a href="#" class="button is-medium is-rounded is-link is-outlined" @click.prevent="showMoreOnMobile">View more details</a>
		</div>
	</div>
</template>

<script>
import TeamLink from "./TeamLink"

export default {
	components: {
		TeamLink,
	},
	props: {
		heading: {
			type: String,
			default: null,
		},
		teams: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			is_mobile_shown_more: false,
		}
	},
	computed: {
		has_divisions() {
			return !!(this.teams && this.teams.length && this.teams[0].division_record)
		},
		has_all_records() {
			return !!(this.teams && this.teams.length && this.teams[0].all_record)
		},
		has_median_records() {
			return !!(this.teams && this.teams.length && this.teams[0].median_record)
		}
	},
	methods: {
		standing_change_to_icon(standing_change) {
			if (standing_change > 2) return "angle-double-up"
			if (standing_change > 0) return "angle-up"
			if (standing_change < -2) return "angle-double-down"
			if (standing_change < 0) return "angle-down"
		},
		showMoreOnMobile() {
			this.is_mobile_shown_more = true
		},
	},
}
</script>
